import type * as React from 'react';

import type { Column } from '@tanstack/react-table';

const SELECT_COLUMN_ID = 'select';

interface ColumnWidthStyleParams<TData> {
  column: Column<TData, unknown>;
  /** Current size — `header.getSize()` for headers, `column.getSize()` for cells. */
  size: number;
  /** Set when the table is resizable; widths then come from column sizing state. */
  tableId?: string;
  /** Explicit widths of the trailing columns that share the remaining space. */
  filledColumnWidths?: Map<string, number>;
}

/**
 * Width style for a header or a cell of `column`. One place for the rule that used to be
 * repeated in every renderer:
 *   - non-resizable table: the column's current size;
 *   - a "filled" trailing column: its computed share of the remaining width (also as max);
 *   - the select column: its fixed definition size, never resized;
 *   - anything else: the size from the resize state.
 */
export function getColumnWidthStyle<TData>({
  column,
  size,
  tableId,
  filledColumnWidths,
}: ColumnWidthStyleParams<TData>): React.CSSProperties {
  if (!tableId) return { width: size };

  const filledWidth = filledColumnWidths?.get(column.id);
  if (filledWidth !== undefined) return { width: filledWidth, maxWidth: filledWidth };

  if (column.id === SELECT_COLUMN_ID) return { width: column.columnDef.size };

  return { width: size };
}

export const isSelectColumn = (columnId: string): boolean => columnId === SELECT_COLUMN_ID;
